import React, { FunctionComponent, useCallback, useEffect } from "react";

import { AggregationFunction } from "@multiweb/clients/dataReader";
import { bridge, createOpenFormModalAction } from "@multiweb/reactCore/bridge";
import { IOWObject } from "@multiweb/types/multiweb";
import { FlagColor } from "@multiweb/types/react";
import { createClearAction, createInitAction } from "../ReportingScalar/action";
import { ColorIntensity, ReportingScalarComponent } from "../ReportingScalarComponent";

const { getDataEventsManager, getInstance } = bridge;

export type ReportingScalarProviderProps = Readonly<{
    /** Background Color */
    backgroundColor: FlagColor;
    /** Caption  */
    caption?: string;
    /** Color Intensity */
    colorIntensity: ColorIntensity;
    /** Form thats opened on click */
    formName?: string;
    /** If its interactive */
    interactive: boolean;
    /** Main value from fetched data */
    value?: string;
    /** Description */
    description?: string;
    /** Type of dataEvent */
    eventName?: string;
    /** Name of class from dataEvents */
    subEventName?: string;
    /** Source of dataEvent */
    eventSource?: string;
    /** Function for component value loading */
    loadData: (...args: Parameters<typeof createInitAction>) => void;
    /** Function for opening a form */
    createOpenFormModalAction: (...args: Parameters<typeof createOpenFormModalAction>) => void;
    /** Function for deleting a data from datKey */
    clearData: (...args: Parameters<typeof createClearAction>) => void;
    /** Name of attribute for loading aggregation value */
    attrName: string;
    /** Aggregation function */
    aggregationFunc: AggregationFunction;
    /** Class name */
    className: string;
    /** Condition for loading data */
    condition: string;
    /** Condition parameters */
    conditionParams?: string[];
    /** Format in which to display resulting value */
    displayFormat: string;
    /** Name of function for formatting aggregation value */
    formatProviderKey: string;
    /** Static method for loading data */
    methodName?: string;
    /** Instance of actualObject */
    instanceId?: string;
    /** Unique id linking to fetched data */
    dataKey: string;
    /** Name of a link to a master object */
    masterLinkToAttr?: string;
}>;

/** Stará se o načítání dat pro komponentu {@link ReportingScalarComponent}*/
export const ReportingScalarProvider: FunctionComponent<ReportingScalarProviderProps> = ({
    backgroundColor,
    colorIntensity,
    interactive,
    createOpenFormModalAction,
    loadData,
    caption,
    description,
    eventName,
    eventSource,
    formName,
    subEventName,
    value,
    attrName,
    aggregationFunc,
    className,
    condition,
    conditionParams,
    displayFormat,
    formatProviderKey,
    methodName,
    dataKey,
    instanceId,
    masterLinkToAttr,
    clearData,
}) => {
    const loadDataCallback = useCallback(() => {
        loadData(
            aggregationFunc,
            attrName,
            className,
            condition,
            dataKey,
            displayFormat,
            formatProviderKey,
            conditionParams,
            instanceId,
            methodName,
        );
    }, [
        loadData,
        aggregationFunc,
        attrName,
        className,
        condition,
        displayFormat,
        formatProviderKey,
        conditionParams,
        instanceId,
        methodName,
        dataKey,
    ]);

    // Loads data after mount, or after any of the arguments used for data fetching changes
    useEffect(() => {
        loadDataCallback();
    }, [loadDataCallback]);

    // Deletes data after unmount, or after dataKey changes
    useEffect(() => {
        return () => clearData(dataKey);
    }, [clearData, dataKey]);

    // Watches for data changes and on change re-fetches them
    useEffect(() => {
        //TODO: This will be needing a change according to concept, thats supposed to be created on the basis of 37576 Architektura react komponent s oddělenou logikou (POC)
        const dataEventsManager = getDataEventsManager();
        const dataEventListener = loadDataCallback;

        if (eventSource && eventName && subEventName && dataEventsManager) {
            dataEventsManager.subscribeDataEvent(eventSource, eventName, subEventName, dataEventListener);
        }
        return () => {
            if (eventSource && eventName && subEventName && dataEventsManager) {
                dataEventsManager.unsubscribeDataEvent(eventSource, eventName, subEventName, dataEventListener);
            }
        };
    }, [
        loadDataCallback,
        eventName,
        eventSource,
        subEventName,
    ]);

    const onClick = () => {
        if (interactive && formName) {
            let actualObject: IOWObject | undefined | null;
            if (instanceId)
                actualObject = getInstance(instanceId);

            createOpenFormModalAction({
                formName,
                condition,
                conditionParamsAttrs: conditionParams,
                conditionParamsObject: actualObject,
                masterLinkToAttr,
                masterObject: actualObject,
            });
        }
    };

    return (
        <ReportingScalarComponent
            {...{
                backgroundColor,
                colorIntensity,
                interactive,
                onClick,
                caption,
                description,
                formName,
                value,
            }}
        />
    );
};
